import React from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { Controller } from 'react-hook-form'
import { Checkbox } from '@mui/material'

/**
 * A form checkbox field component that integrates with react-hook-form.
 * This component wraps Material-UI's Checkbox with react-hook-form's Controller for form state management.
 *
 * @param {Object} props - The component props
 * @param {string} props.name - The name of the field (required for form registration)
 * @param {Object} [props.rules] - The validation rules for react-hook-form
 * @param {Object} props.control - The react-hook-form control object
 * @param {Function} [props.onChange=noop] - Callback fired when the checkbox state changes
 * @param {Object} [props.sx] - The system prop that allows defining system overrides as well as additional CSS styles
 * @param {boolean} [props.disabled] - Whether the checkbox is disabled
 * @param {string} [props.color] - The color of the checkbox
 * @param {string} [props.size] - The size of the checkbox
 * @param {boolean} [props.checked] - Whether the checkbox is checked (controlled component)
 * @param {boolean} [props.defaultChecked] - Whether the checkbox is initially checked (uncontrolled component)
 * @returns {React.ReactElement} The rendered form checkbox field component
 */
const HHFormCheckboxField = ({ name, rules, control, onChange = noop, sx, ...rest }) => {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => {
        const { onBlur, onChange: onChangeHookForm, ..._rest } = field
        return (
          <Checkbox
            checked={field?.value || false}
            onChange={value => {
              onChangeHookForm(value)
              onChange(value)
              onBlur()
            }}
            sx={sx}
            {..._rest}
            {...rest}
          />
        )
      }}
    />
  )
}

HHFormCheckboxField.propTypes = {
  control: PropTypes.object.isRequired,
  name: PropTypes.string,
  rules: PropTypes.object,
  sx: PropTypes.object,
  onChange: PropTypes.func,
}

export default HHFormCheckboxField
